import React, { useEffect } from "react";
import { Box, Table, TableBody, TableCell, TableRow, Skeleton, Divider } from "@mui/material";
import { useQlikObject } from "../../hooks/useQlikObject";
import { useReady } from "../../hooks/useReady";
import { getObject } from "../../services/qlikObjectService";

interface SimpleQlikKpiProps {
  qlikVizName: string;
}

const SimpleQlikKpi: React.FC<SimpleQlikKpiProps> = ({ qlikVizName }) => {
  const { qlikObject } = useReady("", "", qlikVizName);
  const { qlikKpiMeasureTitle, qlikKpiObjectValue } = useQlikObject(qlikVizName);

  useEffect(() => {
    // Fetch KPI object if not already created
    if (!qlikObject) {
      getObject(qlikVizName);
    }
  }, [qlikVizName, qlikObject]);

  // Format currency value if it's a number
  const formatValue = (value: string): string => {
    // Check if value looks like a number (remove commas and check)
    const numericValue = value.replace(/,/g, '');
    if (!isNaN(parseFloat(numericValue)) && isFinite(Number(numericValue))) {
      const num = parseFloat(numericValue);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    }
    return value;
  };

  return (
    <Box className="kpi-item">
      <Table>
        <TableBody>
          <TableRow sx={{ '& td': { border: 0 } }}>
            {/* KPI Label - aligned right */}
            <TableCell 
              align="left" 
              sx={{ 
                fontWeight: 400,
                typography: 'subtitle1',
                color: 'text.secondary',
                fontStyle: 'italic',
                pr: 2,
                py: 0
              }}
            >
              {qlikKpiMeasureTitle}
            </TableCell>
            
            {/* KPI Value - aligned left */}
            <TableCell 
              align="right"
              sx={{ 
                fontWeight: 400,
                typography: 'subtitle1',
                color: 'text.primary',
                pl: 2,
                py: 0
              }}
            >
              {formatValue(qlikKpiObjectValue)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Divider />
    </Box>
  );
};

export default SimpleQlikKpi;
